import React, { useState } from "react";
import { TextField, FormControl, Grid, Tooltip } from "@material-ui/core";
import { Button } from "react-bootstrap";
import { useFormik } from "formik";
import * as Yup from "yup";
import { NotificationManager } from "react-notifications";
import LoadingComponent from "../LoadingComponent/LoadingComponent";
import { CheckCircle, Cancel } from '@material-ui/icons';

export function calcCost(pasteML, printMinutes) {
  const sinterHour = 42;
  const printHour = printMinutes / 60;
  const yearHour = 365 * 24 * 0.75;
  const pasteCost = (625 / 0.8) * pasteML / 1000;
  const laborCost = 48 * 1.5;
  const consumableCost = printHour * 0.30 + sinterHour * 0.75 + 24 + 50;
  const printerCost = 5.40 * printHour;
  const furnaceCost = 3.0 * sinterHour;
  const accesoriesCost = 42000 * (printHour + sinterHour) / (2 * yearHour * 7);
  const maintananceCost = 25000 * (printHour + sinterHour) / (2 * yearHour);
  const totalCost = pasteCost + laborCost + consumableCost + printerCost + furnaceCost + accesoriesCost + maintananceCost;
  return totalCost;
}

export function quickQuote(v, a, z) {
  const paste = 2.793 * v + 0.64 * z + 0.081 * a;
  const printTime = 67 + 77 * z + 10.2 * v;
  return [paste, printTime];
}

export function fitPlate(x, y, z) {
  // 200x200x160 build volume
  if (z > 160)
    return 0;
  return Math.floor((200 + 10) / (x + 10)) * Math.floor((200 + 10) / (y + 10))
}

const validationSchema = Yup.object({
  xDimension: Yup.number()
    .positive("Must be greater than zero")
    .required("Required"),
  yDimension: Yup.number()
    .positive("Must be greater than zero")
    .required("Required"),
  zDimension: Yup.number()
    .positive("Must be greater than zero")
    .required("Required"),
  volume: Yup.number()
    .positive("Must be greater than zero")
    .required("Required"),
  area: Yup.number()
    .positive("Must be greater than zero")
    .required("Required"),
});

const SVGComponent = (props) => (
  <svg
    width={30}
    height={30}
    id="Layer_1"
    xmlns="http://www.w3.org/2000/svg"
    xmlnsXlink="http://www.w3.org/1999/xlink"
    x="0px"
    y="0px"
    viewBox="0 0 122.88 105.98"
    style={{
      enableBackground: "new 0 0 122.88 105.98",
    }}
    xmlSpace="preserve"
    {...props}
  >
    <g>
      <path d="M122.88,105.98H9.59v-0.02c-2.65,0-5.05-1.08-6.78-2.81c-1.72-1.72-2.79-4.11-2.79-6.75H0V0h12.26v93.73h110.62V105.98 L122.88,105.98z M83.37,45.6h19.55c1.04,0,1.89,0.85,1.89,1.89v38.46c0,1.04-0.85,1.89-1.89,1.89H83.37 c-1.04,0-1.89-0.85-1.89-1.89V47.5C81.48,46.46,82.33,45.6,83.37,45.6L83.37,45.6z M25.36,22.07h19.55c1.04,0,1.89,0.85,1.89,1.89 v62c0,1.04-0.85,1.89-1.89,1.89H25.36c-1.04,0-1.89-0.85-1.89-1.89v-62C23.47,22.92,24.32,22.07,25.36,22.07L25.36,22.07 L25.36,22.07z M54.37,8.83h19.54c1.04,0,1.89,0.85,1.89,1.89v75.24c0,1.04-0.85,1.89-1.89,1.89H54.37c-1.04,0-1.89-0.85-1.89-1.89 V10.72C52.48,9.68,53.33,8.83,54.37,8.83L54.37,8.83z" />
    </g>
  </svg>
);

const DisplayQuoteTool = (props) => {
  const [printTime, setPrintTime] = useState<number | null>(null);
  const [requiredVolume, setRequiredVolume] = useState<number | null>(null);
  const [maxCopies, setMaxCopies] = useState<number | null>(null);
  const [twoXTime, setTwoXTime] = useState<number | null>(null);
  const [twoXPaste, setTwoXPaste] = useState<number | null>(null);
  const [fourXTime, setFourXTime] = useState<number | null>(null);
  const [fourXPaste, setFourXPaste] = useState<number | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [printCost, setPrintCost] = useState<number | null>(null);
  const [twoXPrintCost, setTwoXPrintCost] = useState<number | null>(null);
  const [fourXPrintCost, setFourXPrintCost] = useState<number | null>(null);
  const [maxTime, setMaxTime] = useState<number | null>(null);
  const [maxPaste, setMaxPaste] = useState<number | null>(null);
  const [maxPrintCost, setMaxPrintCost] = useState<number | null>(null);

  const resetAll = () => {
    setPrintTime(null);
    setRequiredVolume(null);
    setMaxCopies(null);
    setTwoXTime(null);
    setTwoXPaste(null);
    setFourXTime(null);
    setFourXPaste(null);
    setPrintCost(null);
    setTwoXPrintCost(null);
    setFourXPrintCost(null);
    setMaxTime(null);
    setMaxPaste(null);
    setMaxPrintCost(null);
  }
  function estimate(v, a, x, y, z) {
    let result = {}
    const n = fitPlate(x, y, z)
    if (n >= 1) {
      result["max_copies"] = n
      const [paste, printTime] = quickQuote(v, a, z)
      result["time"] = Math.round(printTime)
      result["paste"] = Math.round(paste)
      result["cost"] = Math.round(calcCost(paste, printTime))
      if (n > 1) {
        const [paste2, printTime2] = quickQuote(2 * v, 2 * a, z)
        result["2x_time"] = Math.round(printTime2)
        result["2x_paste"] = Math.round(paste2)
        result["2x_cost"] = Math.round(calcCost(paste2, printTime2) / 2)
        if (n > 3) {
          const [paste4, printTime4] = quickQuote(4 * v, 4 * a, z)
          result["4x_time"] = Math.round(printTime4)
          result["4x_paste"] = Math.round(paste4)
          result["4x_cost"] = Math.round(calcCost(paste4, printTime4) / 4)
        }
        if (n > 4) {
          const [pasteMax, printTimeMax] = quickQuote(n * v, n * a, z)
          result["max_time"] = Math.round(printTimeMax)
          result["max_paste"] = Math.round(pasteMax)
          result["max_cost"] = Math.round(calcCost(pasteMax, printTimeMax) / n)
        }
      }
    }
    return result
  }

  const formik = useFormik({
    initialValues: {
      xDimension: "",
      yDimension: "",
      zDimension: "",
      volume: "",
      area: "",
    },
    validationSchema,
    onSubmit: async (values) => {
      try {
        setIsLoading(true)
        const result = estimate(values.volume, values.area, values.xDimension, values.yDimension, values.zDimension)
        if (result["max_copies"]) {
          setPrintTime(result["time"]);
          setRequiredVolume(result["paste"]);
          setMaxCopies(result["max_copies"]);
          setTwoXTime(result["2x_time"]);
          setTwoXPaste(result["2x_paste"]);
          setFourXTime(result["4x_time"]);
          setFourXPaste(result["4x_paste"]);
          setPrintCost(result["cost"]);
          setTwoXPrintCost(result["2x_cost"]);
          setFourXPrintCost(result["4x_cost"]);
          setMaxTime(result["max_time"]);
          setMaxPaste(result["max_paste"]);
          setMaxPrintCost(result["max_cost"]);

          NotificationManager.success(
            "Print time calculated successfully",
            "Success",
            3000
          );
        } else {
          resetAll();
          NotificationManager.warning(
            "Part doesn't fit in build plate",
            "Warning",
            3000
          );
        }
        setIsLoading(false)
      } catch (error: any) {
        console.error("Failed to calculate print time:", error);
        setIsLoading(false)

        NotificationManager.error(
          error.response?.data?.message || "Failed to calculate print time",
          "Error",
          5000
        );
      }
    },
  });

  // ✅ Check if all non-disabled fields are filled
  const isFormValid = () =>
    !!formik.values.xDimension &&
    !!formik.values.yDimension &&
    !!formik.values.zDimension &&
    !!formik.values.volume &&
    !!formik.values.area;
  const formatTime = (mins: number | null) => {
    if (mins === null) return "--";
    return `${Math.round(mins / 60).toString()} hr`;
  };
  // Check if parts fit on the build plate based on dimensions
  const checkFit = (copies: number) => {
    return maxCopies ? ((copies <= maxCopies) ? "✅" : "❌") : null;
  };

  // Create part data array with fit check based on dimensions
  let partData = [
    {
      qty: 1,
      time: printTime,
      paste: requiredVolume,
      cost: printCost,
      fits: checkFit(1)
    },
    {
      qty: 2,
      time: twoXTime,
      paste: twoXPaste,
      cost: twoXPrintCost,
      fits: checkFit(2)
    },
    {
      qty: 4,
      time: fourXTime,
      paste: fourXPaste,
      cost: fourXPrintCost,
      fits: checkFit(4)
    },
  ];
  if (maxCopies && maxCopies > 4) {
    partData.push({
      qty: maxCopies,
      time: maxTime,
      paste: maxPaste,
      cost: maxPrintCost,
      fits: checkFit(maxCopies)
    })
  }
  return (
    <div className="flex-d">
      <LoadingComponent
        visible={isLoading}
      ></LoadingComponent>
      <div
        className="dashboard m-5"
        style={{ height: "82vh", overflowX: "hidden" }}
      >
        <div className="text-h-l" >Quote tool</div>

        <div className="dashboard-body">
          <Grid container spacing={5} className="my-3">
            <Grid item xs={12} md={5}>
              <form onSubmit={formik.handleSubmit}>
                <Grid container spacing={2}>

                  {/* Volume */}
                  <Grid item xs={6}>
                    <FormControl fullWidth variant="outlined">
                      <TextField
                        label="Volume (mL)"
                        variant="outlined"
                        name="volume"
                        type="number"
                        value={formik.values.volume}
                        onChange={formik.handleChange}
                        onBlur={formik.handleBlur}
                        error={
                          formik.touched.volume && Boolean(formik.errors.volume)
                        }
                        helperText={
                          formik.touched.volume && formik.errors.volume
                        }
                        InputProps={{
                          endAdornment: <span style={{ color: '#777' }}>mL</span>,
                        }}
                      />
                    </FormControl>
                  </Grid>

                  {/* Area */}
                  <Grid item xs={6}>
                    <FormControl fullWidth variant="outlined">
                      <TextField
                        label="Surface Area (cm²)"
                        variant="outlined"
                        name="area"
                        type="number"
                        value={formik.values.area}
                        onChange={formik.handleChange}
                        onBlur={formik.handleBlur}
                        error={
                          formik.touched.area && Boolean(formik.errors.area)
                        }
                        helperText={
                          formik.touched.area && formik.errors.area
                        }
                        InputProps={{
                          endAdornment: <span style={{ color: '#777' }}>cm<sup>2</sup></span>,
                        }}
                      />
                    </FormControl>
                  </Grid>

                  {/* X Dimension */}
                  <Grid item xs={6}>
                    <FormControl fullWidth variant="outlined">
                      <TextField
                        label="X Dimension (mm)"
                        variant="outlined"
                        name="xDimension"
                        type="number"
                        value={formik.values.xDimension}
                        onChange={formik.handleChange}
                        onBlur={formik.handleBlur}
                        error={
                          formik.touched.xDimension &&
                          Boolean(formik.errors.xDimension)
                        }
                        helperText={
                          formik.touched.xDimension && formik.errors.xDimension
                        }
                        InputProps={{
                          endAdornment: <span style={{ color: '#777' }}>mm</span>,
                        }}
                      />
                    </FormControl>
                  </Grid>

                  {/* Y Dimension */}
                  <Grid item xs={6}>
                    <FormControl fullWidth variant="outlined">
                      <TextField
                        label="Y Dimension (mm)"
                        variant="outlined"
                        name="yDimension"
                        type="number"
                        value={formik.values.yDimension}
                        onChange={formik.handleChange}
                        onBlur={formik.handleBlur}
                        error={
                          formik.touched.yDimension &&
                          Boolean(formik.errors.yDimension)
                        }
                        helperText={
                          formik.touched.yDimension && formik.errors.yDimension
                        }
                        InputProps={{
                          endAdornment: <span style={{ color: '#777' }}>mm</span>,
                        }}
                      />
                    </FormControl>
                  </Grid>

                  {/* Z Dimension */}
                  <Grid item xs={6}>
                    <FormControl fullWidth variant="outlined">
                      <TextField
                        label="Z Dimension (mm)"
                        variant="outlined"
                        name="zDimension"
                        type="number"
                        value={formik.values.zDimension}
                        onChange={formik.handleChange}
                        onBlur={formik.handleBlur}
                        error={
                          formik.touched.zDimension &&
                          Boolean(formik.errors.zDimension)
                        }
                        helperText={
                          formik.touched.zDimension && formik.errors.zDimension
                        }
                        InputProps={{
                          endAdornment: <span style={{ color: '#777' }}>mm</span>,
                        }}
                      />
                    </FormControl>
                  </Grid>

                  {/* Submit Button */}
                  <Grid item xs={12}>
                    <Button
                      variant="primary"
                      disabled={!isFormValid()}
                      type="submit"
                    >
                      Estimate
                    </Button>
                  </Grid>
                </Grid>
              </form>
            </Grid>

            {/* Result Display */}
            <Grid item xs={12} md={6}>
              <div style={{
                padding: "20px",
                border: "1px solid #ddd",
                borderRadius: "8px",
                background: "#f9f9f9",
                fontFamily: 'Graphik-Regular',
                boxShadow: "0px 2px 4px rgba(0,0,0,0.05)"
              }}>
                <Grid container spacing={2}>
                  <Grid item xs={12} style={{ borderBottom: "1px solid #e2dfdf", marginBottom: "10px", paddingBottom: "8px" }}>
                    <div className="text-h-l" style={{
                      fontFamily: 'Graphik-Regular',
                      display: 'flex',
                      justifyContent: 'space-between',
                      alignItems: 'center',
                      fontSize: '18px',
                      fontWeight: 600
                    }}>
                      Print Results <SVGComponent />
                    </div>
                  </Grid>

                  {/* Table Container */}
                  <Grid item xs={12}>
                    <div style={{ width: '100%' }}>
                      {/* Header Row */}
                      <Grid container spacing={2} style={{
                        fontFamily: 'Graphik-Regular',
                        fontSize: '16px',
                        fontWeight: 600,
                        borderBottom: '2px solid #e2dfdf',
                        paddingBottom: '8px',
                        marginBottom: '8px'
                      }}>
                        <Grid item xs style={{ textAlign: 'center' }}><strong>Part Qty</strong></Grid>
                        <Grid item xs style={{ textAlign: 'center' }}><strong>Unit Cost ($)</strong></Grid>
                        <Grid item xs style={{ textAlign: 'center' }}><strong>Print Time (hr)</strong></Grid>
                        <Grid item xs style={{ textAlign: 'center' }}><strong>Paste Volume (mL)</strong></Grid>
                        <Grid item xs style={{ textAlign: 'center' }}><strong>Fits on Plate?</strong></Grid>
                      </Grid>

                      {/* Data Rows */}
                      {partData.map((item, index) => (
                        <Grid
                          container
                          spacing={2}
                          key={item.qty}
                          style={{
                            fontFamily: 'Graphik-Regular',
                            fontSize: '15px',
                            padding: '8px 0',
                            backgroundColor: index % 2 === 0 ? '#f5f5f5' : 'transparent',
                            borderRadius: '4px'
                          }}
                        >
                          <Grid item xs style={{ textAlign: 'center', display: 'flex', justifyContent: 'center', alignItems: 'center' }}>
                            {item.qty}
                          </Grid>
                          <Grid item xs style={{ textAlign: 'center', display: 'flex', justifyContent: 'center', alignItems: 'center' }}>
                            {item.fits === "❌" ?
                              <Tooltip title="Does not fit on Mantle build plate" placement="top">
                                <span style={{ color: '#777', fontSize: '13px' }}>N/A</span>
                              </Tooltip> :
                              (item.cost !== null ? `$${item.cost}` : "--")
                            }
                          </Grid>
                          <Grid item xs style={{ textAlign: 'center', display: 'flex', justifyContent: 'center', alignItems: 'center' }}>
                            {item.fits === "❌" ?
                              <Tooltip title="Does not fit on Mantle build plate" placement="top">
                                <span style={{ color: '#777', fontSize: '13px' }}>N/A</span>
                              </Tooltip> :
                              formatTime(item.time)
                            }
                          </Grid>
                          <Grid item xs style={{ textAlign: 'center', display: 'flex', justifyContent: 'center', alignItems: 'center' }}>
                            {item.fits === "❌" ?
                              <Tooltip title="Does not fit on Mantle build plate" placement="top">
                                <span style={{ color: '#777', fontSize: '13px' }}>N/A</span>
                              </Tooltip> :
                              (item.paste !== null ? `${item.paste} mL` : "--")
                            }
                          </Grid>
                          <Grid item xs style={{ textAlign: 'center', display: 'flex', justifyContent: 'center', alignItems: 'center' }}>
                            {item.fits === null ?
                              "--" :
                              (item.fits === "✅" ?
                                <Tooltip title="Fits on Mantle build plate" placement="top">
                                  <CheckCircle style={{ color: 'green' }} />
                                </Tooltip> :
                                <Tooltip title="Does not fit on Mantle build plate (190 x 190 mm)" placement="top">
                                  <Cancel style={{ color: 'red' }} />
                                </Tooltip>
                              )
                            }
                          </Grid>
                        </Grid>
                      ))}
                    </div>
                  </Grid>
                </Grid>
              </div>
            </Grid>
          </Grid>
        </div>
      </div>
    </div>
  );
};

const QuoteTool = DisplayQuoteTool;

export default QuoteTool;
